
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import { Check, Close, Visibility, FilterList, Search } from '@mui/icons-material';
import { attendanceApi } from '../../../api/attendanceApi';
import { toast } from 'react-toastify';

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

interface RegularizationRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode?: string;
  date: string;
  requestType?: 'Attendance' | 'Overtime' | string;
  requestedCheckIn: string;
  requestedCheckOut: string;
  requestedOtHours?: number | null;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  approverRemarks?: string;
  rejectionReason?: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Pending': return 'warning';
    case 'Approved': return 'success';
    case 'Rejected': return 'error';
    default: return 'default';
  }
};

const formatDate = (iso: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatTime = (iso: string): string => {
  if (!iso) return '-';

  const utcStr = iso.includes('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return iso;

  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
};

const formatRequestType = (value?: string): string => {
  switch ((value || 'Attendance').toLowerCase()) {
    case 'overtime': return 'OT';
    case 'compoff': return 'Comp-Off';
    default: return 'Attendance';
  }
};

const RegularizationRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<RegularizationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reject dialog
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // View details dialog
  const [viewDialog, setViewDialog] = useState<{ open: boolean; request: RegularizationRequest | null }>({
    open: false,
    request: null,
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.getRegularizationRequests({
        status: statusFilter || undefined,
        requestType: requestTypeFilter || undefined,
      });
      setRequests(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch regularization requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [requestTypeFilter, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    setPage(0);
  }, [requestTypeFilter, statusFilter, searchTerm]);

  const filteredRequests = requests.filter((request) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return true;

    return [
      request.employeeName,
      request.employeeCode,
      formatDate(request.date),
      request.requestType || 'Attendance',
      request.requestedCheckIn ? formatTime(request.requestedCheckIn) : '',
      request.requestedCheckOut ? formatTime(request.requestedCheckOut) : '',
      request.requestedOtHours,
      request.reason,
      request.status,
      request.approvedByName,
      request.approverRemarks,
      request.rejectionReason,
    ]
      .filter((value) => value !== undefined && value !== null)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch));
  });
  const paginatedRequests = rowsPerPage === -1
    ? filteredRequests
    : filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleApprove = async (id: string) => {
    setActionLoading(true);
    try {
      await attendanceApi.approveRegularization(id);
      toast.success('Request approved successfully');
      fetchRequests();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to approve request';
      if (err.response?.status === 403) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.id) return;
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await attendanceApi.rejectRegularization(rejectDialog.id, rejectReason);
      toast.success('Request rejected');
      setRejectDialog({ open: false, id: null });
      setRejectReason('');
      fetchRequests();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to reject request';
      if (err.response?.status === 403) {
        toast.error(msg);
        setRejectDialog({ open: false, id: null });
        setRejectReason('');
      } else {
        toast.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        View Regularization Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Employees request corrections for attendance records. Manager reviews and HR/Admin approves or rejects.        
      </Typography>

      {/* Workflow Info */}
      <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Workflow
          </Typography>
          <Typography variant="body2">
            1. Employee submits request → 2. Manager reviews request → 3. HR/Admin approves or rejects
          </Typography>
        </CardContent>
      </Card>

      {/* Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FilterList color="action" />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Request Type</InputLabel>
          <Select
            value={requestTypeFilter}
            label="Request Type"
            onChange={(e) => setRequestTypeFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Attendance">Attendance</MenuItem>
            <MenuItem value="Overtime">OT</MenuItem>
            <MenuItem value="CompOff">Comp-Off</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search requests"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          Showing {filteredRequests.length} of {requests.length} requests
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Employee Code</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Request Type</strong></TableCell>
                <TableCell><strong>Requested Check-In</strong></TableCell>
                <TableCell><strong>Requested Check-Out</strong></TableCell>
                <TableCell><strong>Requested OT</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No regularization requests found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((req) => (
                  <TableRow key={req.id} hover>
                    <TableCell>{req.employeeName || '-'}</TableCell>
                    <TableCell>{req.employeeCode || '-'}</TableCell>
                    <TableCell>{formatDate(req.date)}</TableCell>
                    <TableCell>{formatRequestType(req.requestType)}</TableCell>
                   <TableCell>
                    {req.requestType === 'Overtime' ? '-' : (req.requestedCheckIn ? formatTime(req.requestedCheckIn) : '-')}
                  </TableCell>
                  <TableCell>
                    {req.requestType === 'Overtime' ? '-' : (req.requestedCheckOut ? formatTime(req.requestedCheckOut) : '-')}
                  </TableCell>
                  <TableCell>{req.requestType === 'Overtime' ? (req.requestedOtHours ?? '-') : '-'}</TableCell>
                    <TableCell>{req.reason}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        color={getStatusColor(req.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {req.status === 'Pending' ? (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(req.id)}
                              disabled={actionLoading}
                            >
                              <Check fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRejectDialog({ open: true, id: req.id })}
                              disabled={actionLoading}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setViewDialog({ open: true, request: req })}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
        </>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: null })}>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason *"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejection"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialog({ open: false, id: null }); setRejectReason(''); }}>
            Cancel
          </Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {viewDialog.request && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              <Typography><strong>Employee:</strong> {viewDialog.request.employeeName}{viewDialog.request.employeeCode ? ` (${viewDialog.request.employeeCode})` : ''}</Typography>
              <Typography><strong>Date:</strong> {formatDate(viewDialog.request.date)}</Typography>
              <Typography><strong>Request Type:</strong> {formatRequestType(viewDialog.request.requestType)}</Typography>
              <Typography><strong>Requested Check-In:</strong> {viewDialog.request.requestType === 'Overtime' ? '-' : (viewDialog.request.requestedCheckIn ? formatTime(viewDialog.request.requestedCheckIn) : '-')}</Typography>
              <Typography><strong>Requested Check-Out:</strong> {viewDialog.request.requestType === 'Overtime' ? '-' : (viewDialog.request.requestedCheckOut ? formatTime(viewDialog.request.requestedCheckOut) : '-')}</Typography>
              <Typography><strong>Requested OT Hours:</strong> {viewDialog.request.requestType === 'Overtime' ? (viewDialog.request.requestedOtHours ?? '-') : '-'}</Typography>
              <Typography><strong>Reason:</strong> {viewDialog.request.reason}</Typography>
              <Typography>
                <strong>Status:</strong>{' '}
                <Chip label={viewDialog.request.status} color={getStatusColor(viewDialog.request.status) as any} size="small" />
              </Typography>
              {(viewDialog.request.approvedByName || viewDialog.request.approvedBy) && (
                <Typography><strong>Processed By:</strong> {viewDialog.request.approvedByName || viewDialog.request.approvedBy}</Typography>
              )}
              {viewDialog.request.approvedAt && (
                <Typography><strong>Processed On:</strong> {formatDate(viewDialog.request.approvedAt)}</Typography>
              )}
              {viewDialog.request.approverRemarks && (
                <Typography><strong>Remarks:</strong> {viewDialog.request.approverRemarks}</Typography>
              )}
              {viewDialog.request.rejectionReason && (
                <Typography><strong>Rejection Reason:</strong> {viewDialog.request.rejectionReason}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, request: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegularizationRequestsPage;
